import { allowsOptionalCookies } from './cookieConsent';

// Lightweight, dependency-free logging/tracking for the marketplace buyer journey.
//
// Two concerns are handled here:
//   1. Operational logging  — structured console output for debugging/audit trails.
//                             Errors/warnings always surface; info/debug only in non-prod
//                             to keep the production console quiet.
//   2. Funnel tracking      — `track()` records buyer-journey events (search → cart →
//                             quote → purchase). These are only forwarded to an analytics
//                             sink when the user has accepted optional cookies. The
//                             console trail is unconditional so support can debug a
//                             session from the browser regardless of consent.
//
// Nothing in here may throw — logging must never break a user flow.

const LEVELS = { debug: 10, info: 20, warn: 30, error: 40 };

// Console verbosity is gated by environment, not just the build's NODE_ENV. Deployed
// builds are always NODE_ENV=production, but dev/staging hosts should still surface the
// full buyer-journey trail for debugging — only true production stays quiet (warn/error).
function isVerboseEnv() {
  const nodeEnv = typeof process !== 'undefined' && process.env && process.env.NODE_ENV;
  if (nodeEnv !== 'production') return true;
  try {
    const host = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
    if (host === 'localhost') return true;
    if (host.endsWith('.dev.avsight-ai.com') || host.includes('dev-av-connect') || host.includes('thankful-sand')) return true;
    if (host.endsWith('.stgn.avsight-ai.com') || host.includes('stgn-av-connect') || host.includes('victorious-rock')) return true;
  } catch {
    /* window unavailable — treat as quiet */
  }
  return false;
}

// In production only warnings and errors reach the console; dev/staging log everything.
const CONSOLE_THRESHOLD = isVerboseEnv() ? LEVELS.debug : LEVELS.warn;

// Keys whose values must never be logged (PII / secrets). Matching is case-insensitive
// and substring-based so e.g. `userEmail`, `sessionToken`, `apiKey` are all redacted.
const REDACT_KEYS = ['email', 'token', 'password', 'apikey', 'api_key', 'secret', 'authorization', 'bearer', 'pin'];

// A per-page-load id so a sequence of events can be correlated when reviewing logs.
const sessionTraceId = (() => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `trace-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
})();

function shouldRedact(key) {
  const k = String(key).toLowerCase();
  return REDACT_KEYS.some((bad) => k.includes(bad));
}

/** Shallow-sanitize a props object: redact PII-ish keys, keep it JSON-serializable. */
function sanitize(props) {
  if (!props || typeof props !== 'object') return {};
  const out = {};
  for (const [key, value] of Object.entries(props)) {
    if (shouldRedact(key)) {
      out[key] = '[redacted]';
    } else if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
      out[key] = value;
    } else {
      // Avoid dumping large/circular objects into logs.
      try {
        out[key] = JSON.parse(JSON.stringify(value));
      } catch {
        out[key] = '[unserializable]';
      }
    }
  }
  return out;
}

function emitConsole(level, event, props) {
  if (LEVELS[level] < CONSOLE_THRESHOLD) return;
  // eslint-disable-next-line no-console
  const fn =
    level === 'error' ? console.error
    : level === 'warn' ? console.warn
    : level === 'info' ? console.info
    : console.debug;
  try {
    fn(`[avs:${event}]`, props);
  } catch {
    /* console unavailable — ignore */
  }
}

function forwardToAnalytics(event, props) {
  // Only forward to an analytics sink with explicit optional-cookie consent.
  if (!allowsOptionalCookies()) return;
  try {
    if (typeof window === 'undefined') return;
    // Push onto a dataLayer-style queue. A future analytics SDK can consume this without
    // any further changes to call sites.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...props });
  } catch {
    /* analytics sink unavailable — ignore */
  }
}

/**
 * Core log entry point.
 * @param {'debug'|'info'|'warn'|'error'} level
 * @param {string} event  dot-namespaced event name, e.g. 'cart.add'
 * @param {Record<string, unknown>} [props]
 */
export function logEvent(level, event, props = {}) {
  try {
    const enriched = {
      ...sanitize(props),
      traceId: sessionTraceId,
      ts: new Date().toISOString(),
    };
    emitConsole(level, event, enriched);
  } catch {
    /* logging must never throw */
  }
}

/**
 * Record a buyer-journey funnel event (info level) and, with consent, forward to analytics.
 * @param {string} event  e.g. 'marketplace.search', 'cart.add', 'quote.submit', 'order.placed'
 * @param {Record<string, unknown>} [props]
 */
export function track(event, props = {}) {
  const clean = sanitize(props);
  logEvent('info', event, clean);
  forwardToAnalytics(event, { ...clean, traceId: sessionTraceId });
}

export const logger = {
  debug: (event, props) => logEvent('debug', event, props),
  info: (event, props) => logEvent('info', event, props),
  warn: (event, props) => logEvent('warn', event, props),
  error: (event, props) => logEvent('error', event, props),
};

export default logger;
